<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouterStore } from '@/store/router'
import { useCongregationInvites } from '@/composables/useCongregationInvites'
import { usePublicTheme } from '@/composables/usePublicTheme'

const themeStore = usePublicTheme()
const route = useRouterStore()
const {
  validateInvite, getManagedUsers, createManagedUser, updateManagedUser, deleteManagedUser,
} = useCongregationInvites()

const token = computed(() => route.params.token)

const loading = ref(true)
const errorMsg = ref(null)
const valid = ref(false)
const congregationName = ref('')

const users = ref([])
const loadingUsers = ref(false)

const form = ref({ email: '', password: '', role: 'member' })
const submitting = ref(false)
const formError = ref(null)

const loadUsers = async () => {
  loadingUsers.value = true
  try {
    const result = await getManagedUsers(token.value)
    users.value = result.users || []
  } finally {
    loadingUsers.value = false
  }
}

const load = async () => {
  loading.value = true
  errorMsg.value = null
  if (!token.value) {
    errorMsg.value = 'Enlace inválido: falta el token.'
    loading.value = false
    return
  }
  const res = await validateInvite(token.value)
  if (res.error) {
    errorMsg.value = res.error
  } else if (res.type !== 'manage_users') {
    errorMsg.value = 'Este enlace no permite gestionar usuarios.'
  } else {
    valid.value = true
    congregationName.value = res.congregationName || ''
    document.title = 'Gestionar usuarios'
    await loadUsers()
  }
  loading.value = false
}

const submit = async () => {
  formError.value = null
  if (!form.value.email.trim() || !form.value.password) {
    formError.value = 'Email y contraseña son requeridos.'
    return
  }
  if (form.value.password.length < 6) {
    formError.value = 'La contraseña debe tener al menos 6 caracteres.'
    return
  }
  submitting.value = true
  try {
    const result = await createManagedUser(token.value, {
      email: form.value.email.trim(),
      password: form.value.password,
      role: form.value.role,
    })
    if (result.error) {
      formError.value = result.error
      return
    }
    users.value.push(result)
    form.value = { email: '', password: '', role: 'member' }
  } finally {
    submitting.value = false
  }
}

const changeRole = async (user, role) => {
  if (role === user.role) return
  const previous = user.role
  user.role = role
  const result = await updateManagedUser(token.value, user.uid, { role })
  if (result?.error) {
    user.role = previous
    formError.value = result.error
  }
}

const removeUser = async (user) => {
  if (!window.confirm(`¿Eliminar al usuario "${user.email}"?`)) return
  const result = await deleteManagedUser(token.value, user.uid)
  if (result?.error) {
    formError.value = result.error
    return
  }
  users.value = users.value.filter((u) => u.uid !== user.uid)
}

onMounted(load)
</script>

<template>
  <div class="simple-page">
    <button
      type="button"
      class="theme-toggle-btn"
      :title="themeStore.theme === 'dark' ? 'Modo claro' : 'Modo oscuro'"
      @click="themeStore.toggle()"
    >
      <i :class="themeStore.theme === 'dark' ? 'pi pi-sun' : 'pi pi-moon'" />
    </button>

    <img src="/territorios-icono.png" alt="Logo" class="simple-logo">

    <div v-if="loading" class="simple-loading">
      <i class="pi pi-spin pi-spinner" />
    </div>

    <div v-else-if="!valid" class="simple-error">{{ errorMsg }}</div>

    <template v-else>
      <h1 class="simple-title">Usuarios{{ congregationName ? ` — ${congregationName}` : '' }}</h1>
      <div class="simple-hint">
        Desde este enlace podés crear, cambiar el rol y eliminar usuarios de la congregación.
      </div>

      <div class="users-list">
        <div v-if="loadingUsers" class="simple-loading small"><i class="pi pi-spin pi-spinner" /></div>
        <div v-else-if="!users.length" class="users-empty">Todavía no hay usuarios.</div>
        <div v-else v-for="u in users" :key="u.uid" class="user-row">
          <div class="user-email">{{ u.email }}</div>
          <select class="user-role-select" :value="u.role" @change="changeRole(u, $event.target.value)">
            <option value="member">Miembro</option>
            <option value="admin">Administrador</option>
          </select>
          <button type="button" class="user-delete-btn" title="Eliminar" @click="removeUser(u)">
            <i class="pi pi-trash" />
          </button>
        </div>
      </div>

      <form class="simple-form" @submit.prevent="submit">
        <label class="simple-field-label" for="u-email">Nuevo usuario</label>
        <input
          id="u-email"
          v-model="form.email"
          type="text"
          class="simple-input"
          placeholder="usuario"
        >

        <label class="simple-field-label mt" for="u-pass">Contraseña</label>
        <input
          id="u-pass"
          v-model="form.password"
          type="password"
          class="simple-input"
          placeholder="Mínimo 6 caracteres"
        >

        <label class="simple-field-label mt" for="u-role">Rol</label>
        <select id="u-role" v-model="form.role" class="simple-input">
          <option value="member">Miembro</option>
          <option value="admin">Administrador</option>
        </select>

        <div v-if="formError" class="simple-error-msg">{{ formError }}</div>

        <button type="submit" class="simple-btn" :disabled="submitting">
          {{ submitting ? 'Creando...' : 'Crear usuario' }}
        </button>
      </form>
    </template>
  </div>
</template>

<style scoped>
.simple-page {
  position: relative;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px 20px 48px;
  background: var(--bg-grouped, #f2f2f7);
  text-align: center;
}

.theme-toggle-btn {
  position: fixed;
  top: max(env(safe-area-inset-top, 0px), 16px);
  right: 16px;
  z-index: 10;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  border: 1.5px solid var(--separator, rgba(0, 0, 0, 0.15));
  background: var(--bg-primary, #fff);
  color: var(--label-secondary, #666);
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  -webkit-tap-highlight-color: transparent;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.theme-toggle-btn:active {
  opacity: 0.7;
}

.simple-logo {
  width: 112px;
  height: 112px;
  margin-bottom: 28px;
}

.simple-loading {
  font-size: 2rem;
  color: var(--accent, #007aff);
  margin-top: 40px;
}

.simple-loading.small {
  font-size: 1.2rem;
  margin: 12px 0;
}

.simple-error {
  font-size: 18px;
  color: var(--label-secondary, #666);
  margin-top: 20px;
  max-width: 420px;
}

.simple-title {
  font-size: 26px;
  font-weight: 700;
  color: var(--label-primary, #111);
  margin: 0 0 16px;
}

.simple-hint {
  width: 100%;
  max-width: 420px;
  background: var(--info-soft, #e6f2ff);
  color: var(--label-primary, #333);
  border-radius: 12px;
  padding: 14px 16px;
  font-size: 14px;
  line-height: 1.4;
  margin-top: 4px;
  margin-bottom: 20px;
  text-align: left;
}

.users-list {
  width: 100%;
  max-width: 420px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 8px;
}

.users-empty {
  color: var(--label-secondary, #666);
  font-size: 14px;
  padding: 8px 0;
}

.user-row {
  display: flex;
  align-items: center;
  gap: 8px;
  background: var(--bg-primary, #fff);
  border-radius: 12px;
  padding: 10px 12px;
  border: 1px solid rgba(0, 0, 0, 0.08);
}

.user-email {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: left;
  font-size: 14px;
  color: var(--label-primary, #111);
}

.user-role-select {
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.15);
  background: var(--bg-primary, #fff);
  color: var(--label-primary, #111);
  padding: 6px 8px;
  font-size: 13px;
}

.user-delete-btn {
  border: none;
  background: transparent;
  color: var(--danger, #ff3b30);
  font-size: 16px;
  cursor: pointer;
  padding: 4px 6px;
  display: flex;
  align-items: center;
}

.simple-form {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 420px;
  text-align: left;
  margin-top: 12px;
  padding-top: 16px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}

.simple-field-label {
  font-size: 15px;
  font-weight: 600;
  color: var(--label-secondary, #666);
  margin-bottom: 8px;
}

.simple-field-label.mt {
  margin-top: 22px;
}

.simple-input {
  width: 100%;
  box-sizing: border-box;
  border-radius: 12px;
  border: 1.5px solid rgba(0, 0, 0, 0.15);
  background: var(--bg-primary, #fff);
  padding: 14px 16px;
  font-size: 17px;
  color: var(--label-primary, #111);
}

.simple-error-msg {
  margin-top: 16px;
  color: var(--danger, #ff3b30);
  font-size: 15px;
  text-align: center;
}

.simple-btn {
  margin-top: 20px;
  width: 100%;
  padding: 18px;
  border: none;
  border-radius: 14px;
  background: var(--success, #34c759);
  color: #fff;
  font-size: 17px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  -webkit-tap-highlight-color: transparent;
}

.simple-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
